import { ActionTree } from 'vuex';
import { AxiosInstance, AxiosResponse } from 'axios';
import { handleAllErrors } from '@/utils/errorHandling';
import { decodeBulkLocationRows, decodeBulkMenuRows } from '@/utils/decoding';
import { BulkEditState, RootState } from '../types';
import { state as authState } from '../auth/auth';

const BASE_PATH: string = '/multi-location';

export const actions: ActionTree<BulkEditState, RootState> = {
	/**
	 * Store the locations picked on the settings page so the bulk pages can read them back.
	 *
	 * @param {string[]} locationIds
	 * @return {void}
	 */
	setLocationIds({ commit }, locationIds: string[]): void {
		commit('SET_LOCATION_IDS', locationIds);
	},

	/**
	 * Fetch the menu items across the selected locations (bulk edit menu table).
	 *
	 * @param {BulkMenuQueryParams} params
	 * @return {Promise<void>}
	 */
	async fetchMenuRows({ commit }, params: BulkMenuQueryParams): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<PaginatedResults<BulkMenuItemRow>> = await axiosInst.post(`${BASE_PATH}/menu-items/list`, params);
			commit('SET_MENU_ROWS', decodeBulkMenuRows(data.rows));
			commit('SET_COUNT', data.count);
		}
		catch (error) {
			commit('SET_MENU_ROWS', []);
			commit('SET_COUNT', 0);
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch the selected locations (bulk edit settings table).
	 *
	 * @param {BulkLocationQueryParams} params
	 * @return {Promise<void>}
	 */
	async fetchLocationRows({ commit }, params: BulkLocationQueryParams): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<PaginatedResults<BulkLocationRow>> = await axiosInst.post(`${BASE_PATH}/locations/list`, params);
			commit('SET_LOCATION_ROWS', decodeBulkLocationRows(data.rows));
			commit('SET_COUNT', data.count);
		}
		catch (error) {
			commit('SET_LOCATION_ROWS', []);
			commit('SET_COUNT', 0);
			handleAllErrors(error);
		}
	},

	/**
	 * Tags defined at location level in any of the selected locations, deduped by name. A tag here is
	 * not guaranteed to exist in every selected location.
	 *
	 * @param {string[]} locationIds
	 * @return {Promise<MenuItemTag[] | undefined>}
	 */
	async fetchLocationTags(_, locationIds: string[]): Promise<MenuItemTag[] | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<MenuItemTag[]> = await axiosInst.post(`${BASE_PATH}/location-tags/list`, { location_ids: locationIds });
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	}

};
